import React from "react";
import {
  useParams,
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import { BrandContext } from "../../App";
import { useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";

import Moment from "moment";

import ReactPlayer from "react-player";
import { CallScreen } from "../../components/complex/CallScreen";
import DefaultBadge from "../../components/simple/DefaultBadge";
import DefaultButton from "../../components/simple/DefaultButton";
import {
  ArrowForwardRounded,
  BarChart,
  VolumeUpRounded,
} from "@mui/icons-material";
import PoweredBy from "../../assets/logos/powered-by.png";

import VideoAvatar from "../../components/VideoAvatar/VideoAvatar";

import { useAPI } from "../../hooks/useAPI";

import { useAuth } from "../../contexts/AuthContext";

export function ExternalOnDemandCoach() {
  const { brand, user } = React.useContext(BrandContext);
  const { authToken } = useAuth();
  const { callAPI } = useAPI();
  const [simulationId, setSimulationId] = React.useState(null);
  const [scenarioData, setScenarioData] = React.useState(null);
  const [simulationData, setSimulationData] = React.useState(null);
  const [simulationHistory, setSimulationHistory] = React.useState(null);
  const [simulationLineChartData, setSimulationLineChartData] =
    React.useState(null);
  const [simulationCallURL, setSimulationCallURL] = React.useState("");
  const [simulationCallToken, setSimulationCallToken] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [popupBlocked, setPopupBlocked] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [showCallScreen, setShowCallScreen] = React.useState(true);
  const [isAudio, setIsAudio] = React.useState(false);
  const theme = useTheme();

  const [integrationType, setIntegrationType] = React.useState("unity");
  const [simStarted, setSimStarted] = React.useState(false);

  const [searchParams] = useSearchParams();

  console.log(searchParams);

  const { simulation_id } = useParams();
  const navigate = useNavigate();

  Moment.locale("en");

  function startScenario({ audio }) {
    setSimStarted(true);
    setLoading(true);
    setIsAudio(audio);
    console.log("ExternalScenario -- Starting scenario with ID", simulation_id);
    callAPI("start_on_demand_coaching_simulation_external", {
      method: "POST",
      body: { scenario_id: simulation_id },
    })
      .then((data) => {
        if (data["id"] && data["livekit_url"]) {
          setSimulationCallURL(data["livekit_url"]);
          setSimulationId(data["id"]);
          setSimulationCallToken(data["token"]);
          setSimulationData(data);
          setLoading(false);
        } else {
          setSnackbarMessage(
            data["livekit_url"]
              ? "Starting simulation failed"
              : "No LiveKit URL available",
          );
          setLoading(false);
        }
        console.log("/start_simulation_external", simulationData);
      })
      .catch((err) => {
        setSnackbarMessage("Starting simulation failed");
        console.log(err.message);
        setLoading(false);
        setSimStarted(false);
      });
  }

  React.useEffect(() => {
    if (!searchParams.get("completed") && !searchParams.get("refid")) {
      startScenario({ audio: false });
    }
  }, []);

  React.useEffect(() => {
    if (simulationData?.type) setIntegrationType(simulationData.type);
  }, [simulationData?.type]);

  return (
    <Box sx={{ backgroundColor: theme.palette.background.primary }}>
      <Snackbar
        open={snackbarMessage ? true : false}
        autoHideDuration={5000}
        onClose={() => {
          setSnackbarMessage("");
        }}
        message={snackbarMessage}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          backgroundColor: "transparent",
          padding: theme.containers.paddingDesktop,
          height: "100vh",
          justifyContent: "center",
          "@media (max-width:1080px)": {
            margin: "auto",
          },
        }}
      >
        {!searchParams.get("completed") && !searchParams.get("refid") ? (
          <VideoAvatar
            scenario={{ name: simulationData?.name }}
            config={simulationData || {}}
            simulationType="on_demand_coach"
            forceAudioOnly={integrationType === "audio"}
          />
        ) : (
          <Box
            sx={{
              height: "100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                backgroundColor: "transparent",
                boxShadow:
                  "0px 12px 16px -4px rgba(154, 152, 191, 0.08), 0px 4px 6px -2px rgba(255, 255, 255, 0.03), 0px 2px 2px -1px rgba(255, 255, 255, 0.04)",
                border: `2px solid ${theme.palette.border.tertiary}`,
                borderRadius: theme.shape.radius,
                padding: theme.containers.paddingDesktop,
                margin: "auto 20%",
                "@media (max-width:1080px)": {
                  margin: "auto",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.displayXS.fontSize,
                  fontWeight: theme.typography.fontWeightMedium,
                  lineHeight: theme.typography.displayXS.lineHeight,
                  mb: 5,
                }}
              >
                You’ve finished your session!
              </Typography>
              <Typography
                sx={{
                  fontSize: theme.typography.textMD.fontSize,
                  color: theme.palette.text.gray,
                  fontWeight: theme.typography.fontWeightLight,
                }}
              >
                Want to refine your approach? Hit Practice Again for another
                round. To see how GuruNow can transform your entire team's
                performance, book a full platform demo today.
              </Typography>
              <Box sx={{ display: "flex", gap: "10px", mt: 5 }}>
                <DefaultButton
                  variant="contained"
                  size="sm"
                  onClick={() =>
                    (window.location.href =
                      "https://www.gurunow.ai/contact-form")
                  }
                >
                  Book a Full Demo
                </DefaultButton>
                <DefaultButton
                  size="sm"
                  backgroundColor="transparent"
                  textColor={theme.palette.text.primary}
                  borderColor={theme.palette.border?.brand}
                  onClick={() =>
                    (window.location.href = `/external_ondemandcoach/${simulation_id}`)
                  }
                >
                  Practice Again
                </DefaultButton>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: theme.customSpacing["2xl"],
          right: 0,
          width: 85,
          opacity: 0.5,
        }}
      >
        <img
          src={PoweredBy}
          alt="Powered by"
          style={{ width: "60px", height: "auto" }}
        />
      </Box>
    </Box>
  );
}
